import { atom, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import {
	FilterOperators,
	Form,
	GetFormsInput,
	SubmissionItemField,
	useAddSubmissionsMutation,
	useGetFormByFilterLazyQuery,
} from "../../../generated/nuggetKmsApi/graphql";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { PathName } from "../../../constants/PathName";
import { Constants } from "../../../constants/Constants";
import { useCustomBreadcrumbs } from "../../../contexts/DashboardMenus.recoil";
import { toast } from "react-toastify";

/**
 * Checks if a string represents a valid positive integer.
 * Used to determine if a form URL parameter is a numeric ID or a string code.
 */
const isNumericId = (value: string): boolean => {
	const num = parseInt(value, 10);
	return !isNaN(num) && num > 0 && String(num) === value;
};

/**
 * Builds the GraphQL filter input based on whether the formId is numeric or a string code.
 */
const buildFormFilterInput = (formId: string): GetFormsInput => {
	if (isNumericId(formId)) {
		return {
			filter: {
				fields: {
					id: { val: parseInt(formId, 10), op: FilterOperators.Equal },
				},
			},
		};
	}
	return {
		filter: {
			fields: {
				code: { val: formId, op: FilterOperators.Equal },
			},
		},
	};
};

const formRecoilAtom = atom<Form | null>({
	key: "formRecoilAtom",
	default: null,
});

const selectedSectionIndexAtom = atom<number>({
	key: "selectedSectionIndexAtom",
	default: 0,
});

const submissionItemFieldsAtom = atom<Partial<SubmissionItemField>[]>({
	key: "submissionItemFieldsAtom",
	default: [],
});

const formDetailFormRefRecoilAtom = atom<HTMLFormElement | null>({
	key: "formDetailFormRefRecoilAtom",
	default: null,
});

const isSubmittedAtom = atom<boolean>({
	key: "isSubmittedAtom",
	default: false,
});

export const useGetFormRecoil = () => {
	const form = useRecoilValue(formRecoilAtom);
	return { form };
};

export const useGetSelectedSectionIndexRecoil = () => {
	const { form } = useGetFormRecoil();
	const selectedSectionIndex = useRecoilValue(selectedSectionIndexAtom);

	const totalSections = useMemo(() => form?.sections.length ?? 0, [form?.sections.length]);
	const isFirstSection = useMemo(() => selectedSectionIndex === 0, [selectedSectionIndex]);
	const isLastSection = useMemo(
		() => selectedSectionIndex === totalSections - 1,
		[selectedSectionIndex, totalSections],
	);

	return { selectedSectionIndex, isFirstSection, isLastSection };
};

export const useSetSelectedSectionIndexRecoil = () => {
	const setSelectedSectionIndex = useSetRecoilState(selectedSectionIndexAtom);
	return { setSelectedSectionIndex };
};

export const useGetSubmissionItemFieldsRecoil = () => {
	const submissionItemFields = useRecoilValue(submissionItemFieldsAtom);

	const getSubmissionItemField = useCallback(
		(itemFieldId: number) => {
			return submissionItemFields.find((itemField) => itemField.itemFieldId === itemFieldId);
		},
		[submissionItemFields],
	);

	return { submissionItemFields, getSubmissionItemField };
};

export const useFormProgress = () => {
	const { form } = useGetFormRecoil();
	const { submissionItemFields } = useGetSubmissionItemFieldsRecoil();

	const progress = useMemo(() => {
		const totalItemFields =
			form?.sections.reduce(
				(acc, section) =>
					acc + section.items.reduce((acc, item) => acc + item.itemFields.length, 0),
				0,
			) ?? 0;

		if (totalItemFields === 0) {
			return 0;
		}

		const filledCount = submissionItemFields.filter(
			(field) => field.value != null && field.value !== "",
		).length;

		return filledCount / totalItemFields;
	}, [form, submissionItemFields]);

	return { progress };
};

export const useSetSubmissionItemFieldsRecoil = () => {
	const setSubmissionItemFields = useSetRecoilState(submissionItemFieldsAtom);

	const setSubmissionItemField = useCallback(
		(itemField: Partial<SubmissionItemField>) => {
			setSubmissionItemFields((prev) => {
				const index = prev.findIndex(
					(_itemField) => _itemField.itemFieldId === itemField.itemFieldId,
				);
				if (index === -1) {
					return [...prev, itemField];
				}
				return [...prev.slice(0, index), itemField, ...prev.slice(index + 1)];
			});
		},
		[setSubmissionItemFields],
	);

	return { setSubmissionItemField };
};

export const useResetSubmissionItemFieldsRecoil = () => {
	const resetSubmissionItemFields = useResetRecoilState(submissionItemFieldsAtom);
	return { resetSubmissionItemFields };
};

export const useGetFormDetailFormRefRecoil = () => {
	const formRef = useRecoilValue(formDetailFormRefRecoilAtom);
	return { formRef };
};

export const useSetFormDetailFormRefRecoil = () => {
	const setFormRef = useSetRecoilState(formDetailFormRefRecoilAtom);
	return { setFormRef };
};

export const useGetIsSubmittedRecoil = () => {
	const isSubmitted = useRecoilValue(isSubmittedAtom);
	return { isSubmitted };
};

export const useSetIsSubmittedRecoil = () => {
	const setIsSubmitted = useSetRecoilState(isSubmittedAtom);
	return { setIsSubmitted };
};

// INIT
export const useFormDetailService = () => {
	const { formId } = useParams<{ formId: string }>();
	const history = useHistory();
	const { resetSubmissionItemFields } = useResetSubmissionItemFieldsRecoil();
	const { setCustomBreadcrumbPath, resetCustomBreadcrumbPath } = useCustomBreadcrumbs();

	const [getForm, { data, loading, error }] = useGetFormByFilterLazyQuery({
		context: {
			clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME,
		},
	});
	const setFormRecoil = useSetRecoilState(formRecoilAtom);
	const { setIsSubmitted } = useSetIsSubmittedRecoil();

	// Reset submission item fields and breadcrumbs on init/unmount
	useEffect(() => {
		resetSubmissionItemFields();
		setIsSubmitted(false);
		return () => {
			resetCustomBreadcrumbPath();
		};
	}, [resetSubmissionItemFields, resetCustomBreadcrumbPath, setIsSubmitted]);

	useEffect(() => {
		if (!formId) {
			history.push(PathName.DASHBOARD);
			return;
		}
		const input = buildFormFilterInput(formId);
		getForm({ variables: { input } });
	}, [formId, getForm, history]);

	// Handle API errors for form fetch
	useEffect(() => {
		if (error) {
			toast.error("An error occurred while loading the form. Please try again.");
		}
	}, [error]);

	useEffect(() => {
		if (data && !loading && !error) {
			const form = data.getForms.rows[0];
			if (!form) {
				// Form not found - show toast and redirect to dashboard
				toast.error("Form not found. Redirecting to dashboard.");
				history.push(PathName.DASHBOARD);
				return;
			}
			setFormRecoil(form);

			// Set breadcrumb path: Dashboard > Forms > [Form Name]
			setCustomBreadcrumbPath([{ name: "Forms", url: "no-click" }, { name: form.name }]);
		}
	}, [data, error, loading, setFormRecoil, history, setCustomBreadcrumbPath]);
};

// SUBMIT FORM
export const useSubmitForm = () => {
	const { form } = useGetFormRecoil();
	const { submissionItemFields } = useGetSubmissionItemFieldsRecoil();
	const { formRef } = useGetFormDetailFormRefRecoil();
	const { setIsSubmitted } = useSetIsSubmittedRecoil();

	const [addSubmissions, { loading: addSubmissionsLoading, error: addSubmissionsError }] =
		useAddSubmissionsMutation({
			context: {
				clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME,
			},
		});

	// Handle submission errors with toast
	useEffect(() => {
		if (addSubmissionsError) {
			toast.error("An error occurred while submitting the form. Please try again.");
		}
	}, [addSubmissionsError]);

	const validateForm = useCallback((): boolean => {
		if (!formRef) {
			return false;
		}

		// Add validated class to show invalid field styling
		formRef.classList.add("validated");

		const isValid = formRef.reportValidity();
		if (!isValid) {
			toast.error("Please fill in all required fields.");

			const firstInvalidField = formRef.querySelector(":invalid") as HTMLElement | null;
			if (firstInvalidField) {
				firstInvalidField.scrollIntoView({ behavior: "smooth", block: "center" });
				firstInvalidField.focus();
			}
		}

		return isValid;
	}, [formRef]);

	const submitForm = useCallback(async (): Promise<boolean> => {
		if (!form) {
			return false;
		}

		if (!validateForm()) {
			return false;
		}

		try {
			const result = await addSubmissions({
				variables: {
					input: {
						inputs: [
							{
								primaryFields: {
									formId: form.id,
								},
								associatedFields: {
									submissionItemFields: submissionItemFields.map((field) => ({
										primaryFields: {
											itemFieldId: field.itemFieldId!,
											value: field.value ?? "",
										},
									})),
								},
							},
						],
					},
				},
			});

			if (result.data) {
				toast.success("Form submitted successfully!");
				setIsSubmitted(true);
				return true;
			}
			return false;
		} catch (error) {
			toast.error("An error occurred while submitting the form. Please try again.");
			return false;
		}
	}, [addSubmissions, form, submissionItemFields, validateForm, setIsSubmitted]);

	return { submitForm, validateForm, addSubmissionsLoading };
};
